/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { apiClient } from './client.js';

/** Nav-module keys a role's access maps against (mirrors BE RBAC_MODULES). */
export const RBAC_MODULES = [
  'dashboard',
  'inbox',
  'patients',
  'therapists',
  'schedule',
  'sessions',
  'waitlist',
  'proformas',
  'invoices',
  'payroll',
  'reports',
  'analytics',
  'followUps',
  'soapNotes',
  'timesheet',
  'documents',
  'users',
  'settings',
] as const;
export type RbacModule = (typeof RBAC_MODULES)[number];

/** 0 None · 1 View · 2 Edit · 3 Full (manage / "live"). */
export const ACCESS_LEVELS = [0, 1, 2, 3] as const;

export interface RoleModuleAccess {
  readonly module: string;
  readonly accessLevel: number;
}

export interface Role {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly description: string | null;
  readonly isSystem: boolean;
  readonly clinicId: string | null;
  readonly userCount: number;
  readonly modules: readonly RoleModuleAccess[];
}

export interface CreateRoleInput {
  readonly name: string;
  readonly description?: string;
  readonly modules: readonly RoleModuleAccess[];
}

export const rolesApi = {
  async list(): Promise<readonly Role[]> {
    const { data } = await apiClient.get<readonly Role[]>('/roles');
    return data;
  },
  async create(input: CreateRoleInput): Promise<{ id: string }> {
    const { data } = await apiClient.post<{ id: string }>('/roles', input);
    return data;
  },
  async updateAccess(id: string, modules: readonly RoleModuleAccess[]): Promise<void> {
    await apiClient.patch(`/roles/${id}/access`, { modules });
  },
};
